import { useRef, useCallback } from 'react';
import { Input } from '@/components/ui/input';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ClearableTimeInputProps {
  value: string;
  onChange: (value: string) => void;
  /** data-testid forwarded to the <input> */
  'data-testid'?: string;
  className?: string;
  disabled?: boolean;
}

/**
 * A time input with an explicit "clear" button.
 *
 * iOS Safari's native time picker cannot reliably reset to empty/null,
 * so we provide our own X-button that:
 *  1. Sets the form value to '' (treated as null on save).
 *  2. Imperatively resets the DOM input value via ref (required on iOS).
 */
export function ClearableTimeInput({
  value,
  onChange,
  'data-testid': testId,
  className,
  disabled,
}: ClearableTimeInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleClear = useCallback(() => {
    // Update form state
    onChange('');
    // Imperatively clear the DOM element (iOS Safari caches the display value)
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  }, [onChange]);

  return (
    <div className="relative">
      <Input
        ref={inputRef}
        type="time"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className={cn('pr-8', className)}
        disabled={disabled}
      />
      {value && !disabled && (
        <button
          type="button"
          onClick={handleClear}
          data-testid={testId ? `${testId}-clear` : undefined}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          aria-label="Rensa tid"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}
